import { supabase } from "@/integrations/supabase/client";
import jsPDF from "jspdf";

const sanitizePathSegment = (value: string) => {
  // Avoid special characters in storage object keys (e.g. umlauts)
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9-_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
};

/**
 * Saves a PDF document to the employee's documents folder
 */
export const saveDocumentToEmployeeFile = async (
  employeeId: string,
  documentType: string,
  documentCategory: string,
  pdfDoc: jsPDF,
  userId: string
): Promise<{ success: boolean; error?: string; documentId?: string }> => {
  try {
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const sanitizedType = sanitizePathSegment(documentType);
    const sanitizedCategory = sanitizePathSegment(documentCategory);
    const fileName = `${sanitizedType}_${timestamp}_${Date.now()}.pdf`;
    
    // Convert PDF to blob
    const pdfBlob = pdfDoc.output('blob');
    const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
    
    // Upload to storage (use sanitized path for storage, but keep original category in DB)
    const filePath = `${employeeId}/${sanitizedCategory}/${fileName}`;
    const { error: uploadError } = await supabase.storage
      .from('employee-documents')
      .upload(filePath, file);

    if (uploadError) {
      console.error('Upload error:', uploadError);
      return { success: false, error: uploadError.message };
    }

    // Create document record
    const { data, error: insertError } = await supabase
      .from('employee_documents')
      .insert({
        employee_id: employeeId,
        file_name: fileName,
        file_path: filePath,
        file_size: file.size,
        mime_type: 'application/pdf',
        category: documentCategory,
        description: documentType,
        uploaded_by: userId,
      })
      .select('id')
      .single();

    if (insertError) {
      console.error('Insert error:', insertError);
      return { success: false, error: insertError.message };
    }

    return { success: true, documentId: data.id };
  } catch (error) {
    console.error('Error saving document:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Saves a PDF document to the applicant's documents folder
 */
export const saveDocumentToApplicantFile = async (
  applicantId: string,
  documentType: string,
  documentCategory: string,
  pdfDoc: jsPDF,
  userId: string
): Promise<{ success: boolean; error?: string; documentId?: string }> => {
  try {
    const timestamp = new Date().toISOString().split("T")[0];
    const sanitizedType = sanitizePathSegment(documentType);
    const sanitizedCategory = sanitizePathSegment(documentCategory);
    const fileName = `${sanitizedType}_${timestamp}_${Date.now()}.pdf`;

    const pdfBlob = pdfDoc.output("blob");
    const file = new File([pdfBlob], fileName, { type: "application/pdf" });

    // Use sanitized path for storage, but keep original category in DB
    const filePath = `${applicantId}/${sanitizedCategory}/${fileName}`;
    const { error: uploadError } = await supabase.storage
      .from("applicant-documents")
      .upload(filePath, file);

    if (uploadError) {
      console.error("Upload error:", uploadError);
      return { success: false, error: uploadError.message };
    }

    const { data, error: insertError } = await supabase
      .from("applicant_documents")
      .insert({
        applicant_id: applicantId,
        file_name: fileName,
        file_path: filePath,
        file_size: file.size,
        mime_type: "application/pdf",
        category: documentCategory,
        description: documentType,
        uploaded_by: userId,
      })
      .select("id")
      .single();

    if (insertError) {
      console.error("Insert error:", insertError);
      return { success: false, error: insertError.message };
    }

    return { success: true, documentId: data.id };
  } catch (error) {
    console.error("Error saving applicant document:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Get document category based on template type
 */
export const getDocumentCategory = (templateType: string): string => {
  switch (templateType) {
    case 'employment_contract':
      return 'Arbeitsverträge';
    case 'contract_amendment':
      return 'Vertragsänderungen';
    case 'certificate':
      return 'Zertifikate';
    case 'anniversary':
      return 'Jubiläumsurkunden';
    case 'warning':
      return 'Abmahnungen';
    case 'vehicle_assignment':
      return 'Fahrzeugüberlassung';
    case 'safety_instruction':
      return 'Sicherheitsunterweisungen';
    case 'key_handover':
      return 'Schlüssel-Übergaben';
    default:
      return 'Sonstiges';
  }
};

/**
 * Get human-readable document type name
 */
export const getDocumentTypeName = (templateType: string): string => {
  switch (templateType) {
    case 'employment_contract':
      return 'Arbeitsvertrag';
    case 'contract_amendment':
      return 'Vertragsänderung';
    case 'certificate':
      return 'Zertifikat';
    case 'anniversary':
      return 'Jubiläumsurkunde';
    case 'warning':
      return 'Abmahnung';
    case 'vehicle_assignment':
      return 'Fahrzeugüberlassung';
    case 'safety_instruction':
      return 'Sicherheitsunterweisung';
    case 'key_handover':
      return 'Schlüssel-Übergabeprotokoll';
    default:
      return 'Dokument';
  }
};
